import {useAtom} from "jotai";
import React, {
  useEffect,
  useMemo,
  useState
} from "react";
import {
  ModalBody,
  ModalFooter,
  ModalHeader
} from "../modal/Modal";
import styled from "styled-components";
import {modalController} from "../../store";
import {
  campaignTemporaryListAtom
} from "../../pages/campaign/entity/Info";
import {toast} from "react-toastify";
import {
  deleteTemporary,
  selTemporaryList
} from "../../services/campaign/InfoAxios";

export function TemporaryListModal({
  onSubmit,
  userId,
  onClose
}) {
  const [, setModal] =
      useAtom(modalController);

  useEffect(() => {
    setModal({
      isShow: true,
      width: 640,
      modalComponent: () => (
          <TemporaryList
              onSubmit={onSubmit}
              userId={userId}
              onClose={onClose}
          />
      )
    });

    return () => {
      setModal({
        isShow: false,
        modalComponent: null
      });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

function TemporaryList({
  onSubmit,
  userId,
  onClose
}) {
  const [, setModal] =
      useAtom(modalController);

  const [
    campaignTemporaryList,
    setCampaignTemporaryList
  ] = useAtom(campaignTemporaryListAtom);

  const [selectedItem, setSelectedItem] =
      useState(null);

  const [
    deletingTemporaryId,
    setDeletingTemporaryId
  ] = useState(null);

  const temporaryList = useMemo(
      () =>
          Array.isArray(campaignTemporaryList)
              ? campaignTemporaryList
              : [],
      [campaignTemporaryList]
  );

  const handleClose = () => {
    if (typeof onClose === "function") {
      onClose();
    }
  };

  const handleSelect = item => {
    setSelectedItem(item);
  };

  const handleSubmit = () => {
    if (!selectedItem?.id) {
      toast.warning(
          "임시 저장된 캠페인을 선택해주세요.",
          {
            toastId:
                "temporaryCampaignSelectionRequired"
          }
      );

      return;
    }

    setModal({
      isShow: false,
      modalComponent: null
    });

    onSubmit(selectedItem);
  };

  const reloadTemporaryList = async () => {
    const response =
        await selTemporaryList(userId);

    setCampaignTemporaryList(
        Array.isArray(response)
            ? response
            : []
    );
  };

  const handleDeleteTemporaryItem =
      async temporaryId => {
        if (
            !temporaryId ||
            deletingTemporaryId !== null
        ) {
          return;
        }

        setDeletingTemporaryId(temporaryId);

        try {
          const response =
              await deleteTemporary(temporaryId);

          if (!response) {
            throw new Error(
                "임시저장 캠페인 삭제 실패"
            );
          }

          if (
              selectedItem?.id === temporaryId
          ) {
            setSelectedItem(null);
          }

          await reloadTemporaryList();

          toast.success(
              "임시 저장된 캠페인이 삭제되었습니다.",
              {
                autoClose: 700,
                toastId:
                    "deleteTemporaryCampaign"
              }
          );
        } catch (error) {
          console.error(
              "임시저장 캠페인 삭제 실패:",
              error
          );

          toast.error(
              "임시 저장된 캠페인 삭제에 실패했습니다.",
              {
                toastId:
                    "deleteTemporaryCampaignError"
              }
          );
        } finally {
          setDeletingTemporaryId(null);
        }
      };

  return (
      <ModalLayout>
        <ModalHeader
            title="임시저장 리스트"
            description="작성 중 저장한 캠페인을 선택하여 이어서 작성할 수 있습니다."
            onClose={handleClose}
        />

        <ModalBody>
          <ContentCard>

            {temporaryList.length > 0 ? (
                <TemporaryTable>
                  <TableHeader>
                    <span aria-hidden="true" />
                    <span>캠페인명</span>
                    <span>관리</span>
                  </TableHeader>

                  <TableBody>
                    {temporaryList.map(item => {
                      const selected =
                          selectedItem?.id === item.id;

                      const deleting =
                          deletingTemporaryId ===
                          item.id;

                      return (
                          <TableRow
                              key={item.id}
                              type="button"
                              $selected={selected}
                              aria-pressed={selected}
                              onClick={() =>
                                  handleSelect(item)
                              }
                          >
                            <SelectedMarkCell>
                              <SelectedMark
                                  $selected={selected}
                                  aria-hidden="true"
                              />
                            </SelectedMarkCell>

                            <CampaignNameCell>
                              <CampaignName>
                                {item.name ||
                                    "캠페인명 없음"}
                              </CampaignName>

                              {selected && (
                                  <SelectedText>
                                    선택됨
                                  </SelectedText>
                              )}
                            </CampaignNameCell>

                            <ActionCell>
                              <DeleteButton
                                  type="button"
                                  disabled={
                                      deletingTemporaryId !==
                                      null
                                  }
                                  onClick={event => {
                                    event.stopPropagation();

                                    handleDeleteTemporaryItem(
                                        item.id
                                    );
                                  }}
                              >
                                {deleting
                                    ? "삭제 중"
                                    : "삭제"}
                              </DeleteButton>
                            </ActionCell>
                          </TableRow>
                      );
                    })}
                  </TableBody>
                </TemporaryTable>
            ) : (
                <EmptyState>
                  <EmptyIcon
                      aria-hidden="true"
                  >
                    <svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                    >
                      <path
                          fill="currentColor"
                          d="M5 3h10l4 4v14H5V3Zm9 2H7v14h10V8h-3V5Zm-5 7h6v2H9v-2Zm0 4h6v2H9v-2Z"
                      />
                    </svg>
                  </EmptyIcon>

                  <EmptyTitle>
                    임시 저장된 정보가 없습니다.
                  </EmptyTitle>

                  <EmptyDescription>
                    캠페인 작성 중 임시저장을 하면
                    이곳에서 다시 불러올 수 있습니다.
                  </EmptyDescription>
                </EmptyState>
            )}
          </ContentCard>
        </ModalBody>

        <ModalFooter>
          <CancelButton
              type="button"
              onClick={() => {
                handleClose();

                setModal({
                  isShow: false,
                  modalComponent: null
                });
              }}
          >
            취소
          </CancelButton>

          <SubmitButton
              type="button"
              disabled={
                  !selectedItem ||
                  deletingTemporaryId !== null
              }
              onClick={handleSubmit}
          >
            선택 완료
          </SubmitButton>
        </ModalFooter>
      </ModalLayout>
  );
}

const ModalLayout = styled.div`
  width: 100%;
`;

const ContentCard = styled.section`
  overflow: hidden;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background-color: #fff;
`;

const ContentHeader = styled.div`
  display: flex;
  min-height: 66px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border-bottom: 1px solid #eeeeee;
  background-color: #f9fafb;
`;

const ContentTitle = styled.div`
  margin-bottom: 4px;
  color: #333;
  font-size: var(--font-size-15);
  font-weight: 700;
`;

const ContentDescription = styled.div`
  color: #888;
  font-size: var(--font-size-13);
  line-height: 1.4;
`;

const CountBadge = styled.span`
  display: inline-flex;
  min-width: 42px;
  height: 26px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  padding: 0 9px;
  border: 1px solid #f1d4c2;
  border-radius: 13px;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: var(--font-size-12);
  font-weight: 700;
`;

const TemporaryTable = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns:
    44px minmax(260px, 1fr) 92px;
  min-width: 480px;
  min-height: 42px;
  align-items: center;
  padding: 0 14px;
  border-bottom: 1px solid #e5e5e5;
  background-color: #fafafa;
  color: #777;
  font-size: var(--font-size-13);
  font-weight: 600;

  & > span:last-child {
    text-align: center;
  }
`;

const TableBody = styled.div`
  max-height: calc(54px * 6);
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color:
    #c7c7c7 transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background-color: #c7c7c7;
  }
`;

const TableRow = styled.button`
  display: grid;
  width: 100%;
  grid-template-columns:
    44px minmax(260px, 1fr) 92px;
  min-width: 480px;
  min-height: 54px;
  align-items: center;
  padding: 0 14px;
  border: 0;
  border-bottom: 1px solid #eeeeee;
  background-color:
    ${({$selected}) =>
    $selected
        ? "#fff8f1"
        : "#fff"};
  color: #444;
  text-align: left;
  cursor: pointer;
  transition:
    background-color 0.15s ease;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background-color: #fffaf6;
  }

  &:focus-visible {
    position: relative;
    z-index: 1;
    outline: 1px solid #f5811f;
    outline-offset: -1px;
  }
`;

const SelectedMarkCell = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SelectedMark = styled.span`
  position: relative;
  display: inline-block;
  width: 18px;
  height: 18px;
  box-sizing: border-box;
  border: 1px solid
    ${({$selected}) =>
    $selected
        ? "#f5811f"
        : "#ccc"};
  border-radius: 50%;
  background-color: #fff;

  &::after {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color:
      ${({$selected}) =>
    $selected
        ? "#f5811f"
        : "transparent"};
    content: "";
    transform:
      translate(-50%, -50%);
  }
`;

const CampaignNameCell = styled.span`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
`;

const CampaignName = styled.span`
  overflow: hidden;
  color: #333;
  font-size: var(--font-size-14);
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SelectedText = styled.span`
  display: inline-flex;
  min-width: 43px;
  height: 21px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 11px;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: var(--font-size-11);
  font-weight: 700;
`;

const ActionCell = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DeleteButton = styled.button`
  min-width: 54px;
  height: 30px;
  padding: 0 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  background-color: #fff;
  color: #666;
  font-size: var(--font-size-13);
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: #e03131;
    background-color: #fff5f5;
    color: #e03131;
  }

  &:disabled {
    background-color: #f5f5f5;
    color: #aaa;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  display: flex;
  min-height: 220px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 34px 20px;
  text-align: center;
`;

const EmptyIcon = styled.span`
  display: inline-flex;
  width: 54px;
  height: 54px;
  align-items: center;
  justify-content: center;
  margin-bottom: 13px;
  border-radius: 50%;
  background-color: #f5f5f5;
  color: #aaa;
`;

const EmptyTitle = styled.div`
  margin-bottom: 7px;
  color: #555;
  font-size: var(--font-size-15);
  font-weight: 700;
`;

const EmptyDescription = styled.div`
  color: #999;
  font-size: var(--font-size-13);
  line-height: 1.5;
`;

const SelectedSummary = styled.div`
  display: flex;
  min-height: 48px;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 13px;
  border: 1px solid #f1d4c2;
  border-left: 3px solid #f5811f;
  border-radius: 0 6px 6px 0;
  background-color: #fffaf6;
`;

const SelectedSummaryLabel = styled.span`
  color: #777;
  font-size: var(--font-size-13);
`;

const SelectedSummaryValue = styled.span`
  overflow: hidden;
  color: #333;
  font-size: var(--font-size-14);
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CancelButton = styled.button`
  border: 1px solid #ddd;
  background-color: #fff;
  color: #555;
  cursor: pointer;

  &:hover {
    border-color: #aaa;
    background-color: #f7f7f7;
  }
`;

const SubmitButton = styled.button`
  border: 1px solid #f5811f;
  background-color: #fff4e8;
  color: #f5811f;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: #ffe8d2;
  }

  &:disabled {
    border-color: #ddd;
    background-color: #f5f5f5;
    color: #aaa;
    cursor: not-allowed;
  }
`;